// Trusted Types adapter for runtime-created script and worker URLs.
// It creates URL policies only; script text creation intentionally fails.
import { readRuntimeSecurityPolicy } from '@lentystyle/guard/runtime'

type RuntimeTrustedTypesPolicy = {
  createScriptURL: (input: string) => unknown
}

type RuntimeTrustedTypesFactory = {
  createPolicy: (
    name: string,
    rules: {
      createScriptURL: (input: string) => string
      createScript: () => never
    },
  ) => RuntimeTrustedTypesPolicy
}

type RuntimeTrustedTypesGlobal = typeof globalThis & {
  trustedTypes?: RuntimeTrustedTypesFactory
}

let cachedPolicy: RuntimeTrustedTypesPolicy | null | undefined

// Return a TrustedScriptURL-compatible value when Trusted Types is active.
export function createTrustedRuntimeScriptUrl(url: string): string | unknown {
  const policy = resolveTrustedTypesPolicy()
  // Return the plain URL in browsers without Trusted Types support.
  if (!policy) {
    return url
  }

  return policy.createScriptURL(url)
}

// Create one named Trusted Types policy for runtime URL sinks.
function resolveTrustedTypesPolicy(): RuntimeTrustedTypesPolicy | null {
  // Reuse the current policy because duplicate policy creation can throw.
  if (cachedPolicy !== undefined) {
    return cachedPolicy
  }

  const factory = (globalThis as RuntimeTrustedTypesGlobal).trustedTypes
  // Return null when the browser does not expose Trusted Types.
  if (!factory) {
    cachedPolicy = null
    return cachedPolicy
  }

  const policyName = readRuntimeSecurityPolicy().trustedTypesPolicyName?.trim() || 'lentystyle'
  try {
    cachedPolicy = factory.createPolicy(policyName, {
      createScriptURL: (input) => input,
      createScript: () => {
        throw new TypeError('LentyStyle runtime does not create TrustedScript text.')
      },
    })
  } catch {
    cachedPolicy = null
  }

  return cachedPolicy
}
